import moment from 'moment-timezone';

import { xmLocalStorage } from '@xmatters/common/utils/xmStorage';
import { checkStatus } from '@xmatters/common/utils/http';
import {
  isEBSEmbedded,
  getCASIdToken,
  getEBFDInstanceUrl,
} from '@xmatters/common/context/ebsGlobalContext';

// TOKEN_EXPIRES_BUFFER is a number of seconds to subtract from WebUI /token response's expiresIn
// This is done to account for network latency and timing issues. It should generally be set very generously:
const TOKEN_EXPIRES_BUFFER = 10;

const reusableTokenData = {
  token: null,
  bestBefore: null,
  currentRequest: null,
};

const updateReusableToken = ({ token, expiresIn }) => {
  reusableTokenData.currentRequest = null;
  reusableTokenData.token = token;
  reusableTokenData.bestBefore = moment().add({
    seconds: Math.max(0, expiresIn - TOKEN_EXPIRES_BUFFER),
  });
};

const canReuseToken = () =>
  reusableTokenData.token && moment().isBefore(reusableTokenData.bestBefore);

export const getTokenPromise = (opts = {}) => {
  if (isEBSEmbedded()) {
    return Promise.resolve(getCASIdToken());
  }

  const { force = false, onError } = opts;
  const { currentRequest } = reusableTokenData;

  if (canReuseToken() && !force) {
    return Promise.resolve(reusableTokenData.token);
  }

  if (currentRequest) {
    return currentRequest;
  }

  const newRequest = fetch(`/webapi/xm/token?t=${+new Date()}`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(
      response =>
        response.json().then(responseJson => {
          updateReusableToken(responseJson);
          return Promise.resolve(responseJson.token);
        }),
      (...args) => {
        if (onError) {
          onError(...args);
        }

        return Promise.resolve(reusableTokenData.token);
      },
    );

  reusableTokenData.currentRequest = newRequest;

  return reusableTokenData.currentRequest;
};

const isXmApiCall = uri =>
  uri.indexOf('/webapi/xm/1') > -1 || uri.indexOf('/api/xm/1') > -1;

const isAbeApiCall = uri =>
  uri.indexOf('/webapi/abe/1') > -1 || uri.indexOf('/api/abe/1') > -1;

export const isNewXmApiCall = uri => isXmApiCall(uri) || isAbeApiCall(uri);

export const callXmApi = ({
  uri,
  options,
  hasFile,
  contentType = 'application/json',
}) =>
  getTokenPromise().then(token => {
    const opts = {
      headers: {
        Accept: 'application/json',
        'Content-Type': contentType,
        Authorization: `Bearer ${token}`,
        'X-User-Agent':
          xmLocalStorage.getItem('xmRateLimitHeader') ||
          'com.xmatters.webui.react',
      },
      // override fetch options
      ...options,
    };

    if (hasFile) {
      delete opts.headers['Content-Type'];
    }

    let preppedUri = isAbeApiCall(uri)
      ? uri.replace('/webapi/abe/1', '/api/abe/1')
      : uri.replace('/webapi/xm/1', '/api/xm/1');

    if (isEBSEmbedded()) {
      preppedUri = getEBFDInstanceUrl() + preppedUri;
    }

    return fetch(preppedUri, opts);
  });
